// src/pages/PatientTodaySimpleView.jsx
//
// The "Simplified" Today screen (Settings → Today screen). One job: say the
// single most important thing the patient should do next, and show everything
// else as a glance. Counterpart to PatientTodayView ("Metric-oriented"), fed
// the same summary/journey/labReminders by PatientTodayPage, and built on the
// same buildAttention() so both screens agree on what needs attention.
//
// "Up next" is the first attention row — lab-day reminders first, then open
// care plan items, unreviewed results, an unbooked follow-up. When nothing is
// pending it becomes an all-clear card pointing at the next milestone, so the
// card is never empty.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import VisitReadyCard from '../components/VisitReadyCard';
import { formatShortDate, AskPreviuBar } from './PatientTodayPage';
import { formatMarkerText } from '../utils/markerNames';
import { buildAttention, summarizeHealthScore } from './PatientTodayView';

const SPARK_W = 120;
const SPARK_H = 30;

function sparkPoints(scores) {
  if (scores.length < 2) return null;
  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const span = max - min || 1;
  return scores
    .map((v, i) => `${(i / (scores.length - 1)) * SPARK_W},${SPARK_H - 3 - ((v - min) / span) * (SPARK_H - 6)}`)
    .join(' ');
}

export default function PatientTodaySimpleView({ patient, summary, journey, labReminders = [] }) {
  const navigate = useNavigate();

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const { attentionRows, pendingCount } = buildAttention({ summary, journey, labReminders });
  const { past, score, trendPct } = summarizeHealthScore(journey);
  const next = attentionRows[0] || null;
  const moreCount = Math.max(0, pendingCount - 1);
  const spark = sparkPoints(past.map(p => p.healthScore).filter(v => v != null));

  const total = summary.totalActionItems ?? 0;
  const open = summary.openActionItems ?? 0;
  const done = Math.max(0, total - open);

  const upcoming = journey.nextMilestone;
  const latest = journey.latestMilestone;

  return (
    <div className="page-shell page-shell--wide today-page today-simple">
      <header className="today-simple-greet">
        <div className="today-simple-eyebrow">{greeting}</div>
        <h1 className="today-simple-name">Hi, {patient?.firstName || 'there'}</h1>
        <p className="today-simple-sub">Your health, made simple.</p>
      </header>

      <VisitReadyCard />

      {/* ── Up next ── */}
      <section className={`card today-next ${next ? '' : 'today-next--clear'}`} aria-label="Up next">
        <div className="today-simple-eyebrow">{next ? 'Up next' : 'All clear'}</div>
        <h2 className="today-next-title">{next ? next.title : 'You’re all caught up'}</h2>
        {next ? (
          <div className="today-next-meta"><span className="today-next-dot" aria-hidden="true" />{next.meta}</div>
        ) : (
          <div className="today-next-meta">
            {upcoming
              ? `Next check-in: ${formatMarkerText(upcoming.label)} · ${formatShortDate(upcoming.date)}`
              : 'Nothing needs your attention right now.'}
          </div>
        )}
        <div className="today-next-footer">
          {next ? (
            <button type="button" className="btn btn-primary" onClick={() => navigate(next.to)}>
              {next.cta || 'View details'}
            </button>
          ) : (
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/patient/chat')}>
              Ask Previu about your health
            </button>
          )}
          {moreCount > 0 && (
            <button type="button" className="today-next-more" onClick={() => navigate('/patient/care-plan')}>
              +{moreCount} more thing{moreCount === 1 ? '' : 's'} to do
            </button>
          )}
        </div>
      </section>

      {/* ── Glance tiles ── */}
      <div className="today-tiles">
        <button type="button" className="card today-tile" onClick={() => navigate('/patient/timeline')}>
          <span className="today-simple-eyebrow">Health score</span>
          <span className="today-tile-big">{score != null ? Math.round(score) : '—'}</span>
          <span className="today-tile-meta" style={trendPct != null ? { color: trendPct >= 0 ? 'var(--teal)' : 'var(--amber-text)', fontWeight: 600 } : undefined}>
            {trendPct != null
              ? `${trendPct >= 0 ? '▲' : '▼'} ${Math.abs(trendPct)}% since ${formatShortDate(past[0].date)}`
              : 'Upload a report to start'}
          </span>
          {spark && (
            <svg className="today-tile-spark" viewBox={`0 0 ${SPARK_W} ${SPARK_H}`} preserveAspectRatio="none" aria-hidden="true">
              <polyline points={spark} fill="none" stroke="var(--teal)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>

        <button type="button" className="card today-tile" onClick={() => navigate('/patient/care-plan')}>
          <span className="today-simple-eyebrow">Care plan</span>
          {total > 0 ? (
            <>
              <span className="today-tile-big">{done}<span className="today-tile-of"> of {total}</span></span>
              <span className="today-tile-meta">{open === 0 ? 'All done' : 'steps completed'}</span>
              <span className="today-tile-steps" aria-hidden="true">
                {Array.from({ length: Math.min(total, 8) }, (_, i) => (
                  <i key={i} className={i < Math.min(done, 8) ? 'on' : ''} />
                ))}
              </span>
            </>
          ) : (
            <>
              <span className="today-tile-big">—</span>
              <span className="today-tile-meta">No steps yet</span>
            </>
          )}
        </button>
      </div>

      {/* ── One insight, only when there is a real one ── */}
      {latest && (
        <button
          type="button"
          className="today-insight"
          onClick={() => navigate(latest.reportId ? `/patient/results/${latest.reportId}` : '/patient/timeline')}
        >
          <span className="today-insight-icon" aria-hidden="true"><Icon name="document" size={16} /></span>
          <span className="today-insight-text">
            <strong>Latest update</strong> · {formatMarkerText(latest.label)}
          </span>
          <Icon name="chevronRight" size={14} />
        </button>
      )}

      <AskPreviuBar placeholder="Ask Previu anything…" />
    </div>
  );
}
